import lodash from 'lodash';
import { goBack } from 'react-router-redux';

import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import {
    toQuery,
    toMutation,
    aliasNodeWithArgs,
    nodeWithArgs,
    node,
    enumeration,
} from 'common/util/queryHelper';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import {
    getDescriptors,
    calculateSaveButtonDisableState,
} from 'indexDocuments/selectors';
import { binderInstitutionIdSelector } from 'common/selectors';
import {
    saveIndexingRestoreInfo,
    loadIndexingRestoreInfo,
} from 'indexDocuments/util/restoreStorage';
import { serializeToStringValue } from 'common/util/dataTypeValues';
import { decrementUnfiledDocuments } from '../../binder/actions/index';

export const INDEX_DOCUMENT_DOCUMENT_PERMISSION = 'INDEX_DOCUMENT_DOCUMENT_PERMISSION';
export const INDEX_DOCUMENT_UNLOAD = 'INDEX_DOCUMENT_UNLOAD';
export const INDEX_DOCUMENT_RESET = 'INDEX_DOCUMENT_RESET';
export const INDEX_DOCUMENT_RESTORE = 'INDEX_DOCUMENT_RESTORE';
export const INDEX_DOCUMENT_WRITE_RESTORE = 'INDEX_DOCUMENT_WRITE_RESTORE';
export const INDEX_DOCUMENT_ASCERTAIN_LOADING = 'INDEX_DOCUMENT_ASCERTAIN_LOADING';
export const INDEX_DOCUMENT_ASCERTAIN_RESULT = 'INDEX_DOCUMENT_ASCERTAIN_RESULT';
export const INDEX_DOCUMENT_SAVESTART = 'INDEX_DOCUMENT_SAVESTART';
export const INDEX_DOCUMENT_SAVEFAILED = 'INDEX_DOCUMENT_SAVEFAILED';
export const INDEX_DOCUMENT_SAVECOMPLETE = 'INDEX_DOCUMENT_SAVECOMPLETE';
export const INDEX_DOCUMENT_NOTIFY_LOADING = 'INDEX_DOCUMENT_NOTIFY_LOADING';
export const INDEX_DOCUMENT_LOAD_DOCUMENT = 'INDEX_DOCUMENT_LOAD_DOCUMENT';
export const INDEX_DOCUMENT_FAILED_LOAD_DOCUMENT = 'INDEX_DOCUMENT_FAILED_LOAD_DOCUMENT';
export const INDEX_DOCUMENT_LOAD_DESCRIPTORS = 'INDEX_DOCUMENT_LOAD_DESCRIPTORS';
export const INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPES_VISIBILITY = 'INDEX_DOCUMENT_SHOW_DOCUMENT_TYPES';
export const INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPE_FILTER = 'INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPE_FILTER';
export const INDEX_DOCUMENT_SELECT_DOCUMENT_TYPE = 'INDEX_DOCUMENT_SELECT_DOCUMENT_TYPE';
export const INDEX_DOCUMENT_DESELECT_DOCUMENT_TYPE = 'INDEX_DOCUMENT_DESELECT_DOCUMENT_TYPE';
export const INDEX_DOCUMENT_HIGHLIGHT_DOCUMENT_TYPE = 'INDEX_DOCUMENT_HIGHLIGHT_DOCUMENT_TYPE';
export const UPDATE_TRIGGERING_SENDING_DOCUMENT_STATUS = 'UPDATE_TRIGGERING_SENDING_DOCUMENT_STATUS';

const indexTypeMap = {
    initialIndex: enumeration('INITIAL_INDEX'),
    index: enumeration('INDEX'),
    reindex: enumeration('REINDEX'),
};

const descriptorNamesOfIntegrations = [
    'velos_status',
    'iqvia_status',
    'saama_status',
    'connexus_status'
];

export function unloadIndexDocument() {
    return {
        type: INDEX_DOCUMENT_UNLOAD,
    };
}

export function loadDocumentPermission(documentId) {
    return dispatch => {
        const query = nodeWithArgs('documentPermission', {
            id: documentId,
        }, [
            'id',
            'binderId',
            'documentTypeId',
            'isIndexed',
            'canInitialIndex',
            'canIndex',
            'canReindex',
            'canManageIntegration'
        ]);

        return queryGraphQL(toQuery(query)).then(response => {

            if (response.error) {
                dispatch(addApplicationMessage('error', 'You do not have permission to index this document.'));
                dispatch(goBack());
            } else {
                const {
                    id,
                    binderId,
                    documentTypeId,
                    isIndexed,
                    canInitialIndex,
                    canIndex,
                    canReindex,
                    canManageIntegration
                } = response.data.documentPermission;
                dispatch(writeDocumentPermission({
                    id,
                    binderId,
                    documentTypeId,
                    isIndexed,
                    canInitialIndex,
                    canIndex,
                    canReindex,
                    canManageIntegration
                }));
            }
        });
    };
}

export function resetIndexDocument() {
    return (dispatch, getState) => {
        const storeState = getState();
        const {
            indexDocument: {
                originalDocumentTypeId,
                originalSelectedDescriptors,
                originalDescriptorValues,
            },
        } = storeState;
        return dispatch(writeResetIndexDocument({
            documentTypeId: originalDocumentTypeId,
            descriptors: originalSelectedDescriptors,
            descriptorValues: originalDescriptorValues,
        }));
    };
}

export function restoreLastSavedDocument() {
    return (dispatch, getState) => {
        const storeState = getState();
        const {
            indexDocument: {
                restoreDocumentTypeId,
                restoreSelectedDescriptors,
                restoreDescriptorValues,
            },
        } = storeState;
        return dispatch(({
            type: INDEX_DOCUMENT_RESTORE,
            payload: {
                documentTypeId: restoreDocumentTypeId,
                descriptors: restoreSelectedDescriptors,
                descriptorValues: restoreDescriptorValues,
            },
        }));
    };
}

function formatInputValues(dataType, values) {
    return lodash.map(values, value => {
        return serializeToStringValue(dataType, value.value);
    });
}

export function ascertainDocument(binderId, shouldRedirectToSDFAUponSuccessfulSave) {
    return (dispatch, getState) => {
        const storeState = getState();
        const { disabled: cannotSave } = calculateSaveButtonDisableState(storeState);
        if (cannotSave) {
            return;
        }
        dispatch(writeAscertainmentLoading(true));
        const {
            document: { id: documentId },
            selectedDocumentTypeId,
            selectedDescriptors
        } = storeState.indexDocument;
        const descriptors = getDescriptors(storeState);
        const documentTypeInput = {
            id: selectedDocumentTypeId,
        };
        const availableIntegrationDescriptorIds = lodash.map(
            getIntegrationDescriptors(selectedDescriptors),
            descriptor => descriptor.id
        );
        const descriptorsInput = lodash.map(descriptors, ({ id, descriptor: { dataType }, values }) => {
            let correctUpdatedValues = values || [];

            if (availableIntegrationDescriptorIds.includes(id) && !lodash.isEmpty(correctUpdatedValues)) {
                correctUpdatedValues = [{
                    value: lodash.get(values[0], 'editorValue', null)
                }];
            }

            return {
                id,
                values: formatInputValues(dataType, correctUpdatedValues),
            };
        });

        const query = toQuery(nodeWithArgs('ascertainDocument', {
            binderId,
            documentId,
            documentType: documentTypeInput,
            descriptors: descriptorsInput,
        }));

        return queryGraphQL(query).then(response => {
            dispatch(writeAscertainmentLoading(false));
            if (response.errors) {
                dispatch(writeAscertainmentResult(null));
                const errMessage = 'There was an issue saving the document';
                dispatch(addApplicationMessage('error', errMessage));
            }
            if (response.data.ascertainDocument) {
                dispatch(writeAscertainmentResult(true));
                return dispatch(saveIndexDocument(shouldRedirectToSDFAUponSuccessfulSave));
            } else {
                dispatch(writeAscertainmentResult(false));
            }
        });
    };
}

export function closeAscertainmentModal() {
    return dispatch => {
        dispatch(writeAscertainmentLoading(false));
        dispatch(writeAscertainmentResult(null));
    };
}

export function saveIndexDocument(shouldRedirectToSDFAUponSuccessfulSave) {
    return (dispatch, getState) => {
        const storeState = getState();
        const { disabled: cannotSave } = calculateSaveButtonDisableState(storeState);
        if (cannotSave) {
            return;
        }
        dispatch(writeSaveStart());
        const {
            document: { id: documentId },
            indexingType,
            selectedDocumentTypeId,
            selectedDescriptors,
            descriptorValues,
        } = storeState.indexDocument;
        const descriptors = getDescriptors(storeState);
        const institutionId = binderInstitutionIdSelector(storeState);
        const documentTypeInput = {
            id: selectedDocumentTypeId,
        };
        const availableIntegrationDescriptorIds = lodash.map(
            getIntegrationDescriptors(selectedDescriptors),
            descriptor => descriptor.id
        );
        const descriptorsInput = lodash.map(descriptors, ({ id, descriptor: { dataType }, values }) => {
            let correctUpdatedValues = values || [];

            if (availableIntegrationDescriptorIds.includes(id) && !lodash.isEmpty(correctUpdatedValues)) {
                correctUpdatedValues = [{
                    value: lodash.get(values[0], 'editorValue', null)
                }];
            }

            return {
                id,
                values: formatInputValues(dataType, correctUpdatedValues),
            };
        });

        const query = nodeWithArgs('indexDocument', {
            documentId,
            institutionId,
            type: indexTypeMap[indexingType],
            documentType: documentTypeInput,
            descriptors: descriptorsInput,
        });

        return mutateGraphQL(toMutation(query)).then(response => {
            if (response.errors) {
                dispatch(writeSaveFailed());
                dispatch(addApplicationMessage('error', 'An error occurred when saving the document'));
            } else {
                saveIndexingRestoreInfo({
                    documentTypeId: selectedDocumentTypeId,
                    descriptors: selectedDescriptors,
                    descriptorValues: descriptorValues,
                });

                checkAndTriggerSendingDocumentToIntegrations()(dispatch, storeState);
                dispatch(writeSaveComplete(shouldRedirectToSDFAUponSuccessfulSave));
                if (indexingType === 'initialIndex' && !shouldRedirectToSDFAUponSuccessfulSave) {
                    dispatch(decrementUnfiledDocuments());
                }
            }
        });
    };
}

export function getIntegrationDescriptors(selectedDescriptors) {
    return Object
        .values(selectedDescriptors)
        .filter(descriptor => descriptorNamesOfIntegrations.includes(descriptor.name));
}

export function checkAndTriggerSendingDocumentToIntegrations() {
    return async (dispatch, storeState) => {
        const {
            document: {
                id: documentId
            },
            binder: { id: binderId },
            selectedDescriptors,
            descriptorValues,
            selectedDocumentTypeId
        } = storeState.indexDocument;
        const documentTypeId = selectedDocumentTypeId || lodash.get(storeState.indexDocument, 'document.documentType.id', '');
        const institutionId = binderInstitutionIdSelector(storeState);

        const availableIntegrationDescriptors = getIntegrationDescriptors(selectedDescriptors);

        if (lodash.isEmpty(availableIntegrationDescriptors)) {
            return;
        }

        const descriptorsInput = availableIntegrationDescriptors
            .map(descriptor => {
                const currentDescriptorData = descriptorValues[descriptor.id];
                const isAvailableValue = !lodash.isEmpty(currentDescriptorData.values);
                const isEditMode = !lodash.isEmpty(currentDescriptorData.values) && currentDescriptorData.values[0].editMode;

                let value = '';
                if (isEditMode) {
                    value = currentDescriptorData.values[0].editorValue;
                } else if (isAvailableValue) {
                    value = currentDescriptorData.values[0].value;
                }

                return {
                    id: descriptor.id,
                    name: descriptor.name,
                    displayName: descriptor.displayName,
                    value
                };
            })
            .filter(descriptor => descriptor.value.toLowerCase() === 'ready');

        if (lodash.isEmpty(descriptorsInput)) {
            return;
        }

        const query = nodeWithArgs('triggerSendDocumentToIntegrations', {
            institutionId,
            binderId,
            documentId,
            documentTypeId,
            descriptors: descriptorsInput
        });

        const {
            errors,
            data: { triggerSendDocumentToIntegrations }
        } = await mutateGraphQL(toMutation(query));

        if (errors) {
            dispatch(updateTriggeringSendingDocumentStatusAction(false));
            dispatch(addApplicationMessage('error', 'An error occurred when triggering sending document to integrations.'));
        }

        dispatch(updateTriggeringSendingDocumentStatusAction(triggerSendDocumentToIntegrations));
    };
}

function parseIfString(value) {
    if (lodash.isString(value) && !lodash.isEmpty(value)) {
        return JSON.parse(value);
    }
    return value;
}

export function loadDocument(documentId, binderId, indexingType, initialDocumentTypeId) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        dispatch(writeLoadingDocument());
        let queryName, queryArgs, queryProjection;
        const isInitialIndex = indexingType === 'initialIndex';
        if (isInitialIndex) {
            queryName = 'unfiledDocuments';
            queryArgs = { query: { documentId, id: binderId, institutionId } };
            queryProjection = [
                'id',
                'name',
                'documentStatus',
                'conversionStatus',
                'uploadDate',
                'binderId',
                node('uploadUser', [
                    'displayName',
                    'emailAddress',
                ]),
                nodeWithArgs('renditions', {
                    type: enumeration('VIEW'),
                }, [
                    'objectName',
                    'containerId',
                    'prettyFileName',
                    'fileExt',
                    'mimeType',
                    'fileSize',
                    'filePurpose',
                    'createdDate',
                    'type',
                    node('downloadRequest', [
                        'tempUrl'
                    ])
                ])
            ];
        } else {
            queryName = 'documentForIndex';
            queryArgs = { id: documentId, binderId };
            queryProjection = [
                'id',
                'name',
                'documentStatus',
                'conversionStatus',
                'uploadDate',
                'binderId',
                node('uploadUser', [
                    'displayName',
                    'emailAddress',
                ]),
                node('documentType', [
                    'id',
                ]),
                node('descriptors', [
                    'id',
                    'dataType',
                    'displayName',
                    'values',
                ]),
                nodeWithArgs('renditions', {
                    type: enumeration('VIEW'),
                }, [
                    'objectName',
                    'containerId',
                    'prettyFileName',
                    'fileExt',
                    'mimeType',
                    'fileSize',
                    'filePurpose',
                    'createdDate',
                    'type',
                    node('downloadRequest', [
                        'tempUrl'
                    ])
                ])
            ];
        }

        const documentQuery = aliasNodeWithArgs('documents', queryName, queryArgs, queryProjection);
        const documentTypes = node('me', [
            node('documentTypes', [
                'id',
                'name',
                'displayName',
                'description',
                'autonameTemplate',
                'permissions',
            ]),
        ]);

        const binderQuery = nodeWithArgs('binder', {
            id: binderId,
        }, [
            'id',
            'title',
        ]);

        const binderIndexingMetadata = nodeWithArgs('getBinderIndexingMetadata', {
            binderId
        });

        return queryGraphQL(
            toQuery([
                documentQuery,
                documentTypes,
                binderQuery,
                binderIndexingMetadata,
            ]),
        ).then(response => {
            if (response.errors) {
                dispatch(writeFailedDocumentLoad());
                dispatch(addApplicationMessage('error', 'An error occurred when loading the document'));
            } else {
                const {
                    documents,
                    me: {
                        documentTypes
                    },
                    binder,
                    getBinderIndexingMetadata: binderIndexingMetadata={}
                } = response.data;
                const document = lodash.isArray(documents) ? lodash.head(documents) : documents;
                const documentTypeId = lodash.get(document, 'documentType.id', initialDocumentTypeId);
                if (documentTypeId) {
                    dispatch(loadDescriptorsForDocumentType(documentTypeId));
                }
                dispatch(writeDocument({
                    document,
                    documentTypes,
                    documentTypeId,
                    binder,
                    indexingType,
                    binderIndexingMetadata: parseIfString(binderIndexingMetadata),
                }));
                dispatch(writeRestoreIndexDocument(loadIndexingRestoreInfo()));
            }
        });
    };
}

function loadDescriptorsForDocumentType(documentTypeId) {
    return (dispatch, getState) => {
        if (lodash.isNil(documentTypeId)) {
            return null;
        }

        const descriptorsQuery = node('me', [
            nodeWithArgs('documentType', {
                documentTypeId,
            }, [
                node('descriptors', [
                    'id',
                    'name',
                    'displayName',
                    'dataType',
                    'description',
                    'displayOrder',
                    'isRequiredForDocumentType',
                    'isActiveForDocumentType',
                    'dataSourceId',
                    'systemName',
                    node('dataset', [
                        'id',
                        'dataType',
                        'values',
                    ]),
                ]),
            ]),
        ]);

        return queryGraphQL(toQuery(descriptorsQuery)).then(response => {
            if (response.errors) {
                //TODO: Handle errors
            } else {
                const {
                    me: {
                        documentType: {
                            descriptors,
                        },
                    },
                } = response.data;
                if (!lodash.isEmpty(descriptors)) {
                    const state = getState();
                    const { binderIndexingMetadata } = state.indexDocument;
                    dispatch(writeDescriptorsForDocumentType(descriptors, binderIndexingMetadata));
                }
            }
        });
    };
}

export function changeDocumentTypeFilter(newValue) {
    return {
        type: INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPE_FILTER,
        payload: newValue,
    };
}

export function highlightDocumentType(documentTypeId) {
    return {
        type: INDEX_DOCUMENT_HIGHLIGHT_DOCUMENT_TYPE,
        payload: documentTypeId,
    };
}

export function selectDocumentType(documentTypeId) {
    return dispatch => {
        dispatch(writeSelectDocumentTypeId(documentTypeId));
        dispatch(loadDescriptorsForDocumentType(documentTypeId));
    };
}

export function changeDocumentTypesVisibility(value) {
    return {
        type: INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPES_VISIBILITY,
        payload: value,
    };
}

export function deselectDocumentType() {
    return {
        type: INDEX_DOCUMENT_DESELECT_DOCUMENT_TYPE,
    };
}

function writeLoadingDocument() {
    return {
        type: INDEX_DOCUMENT_NOTIFY_LOADING,
    };
}

function writeResetIndexDocument({
    documentTypeId,
    descriptors,
    descriptorValues,
}) {
    return {
        type: INDEX_DOCUMENT_RESET,
        payload: {
            documentTypeId,
            descriptors,
            descriptorValues,
        },
    };
}

function writeRestoreIndexDocument({
    documentTypeId,
    descriptors,
    descriptorValues,
}) {
    return {
        type: INDEX_DOCUMENT_WRITE_RESTORE,
        payload: {
            documentTypeId,
            descriptors,
            descriptorValues,
        },
    };
}

function writeDocument({
    document,
    documentTypeId,
    documentTypes,
    binder,
    indexingType,
    binderIndexingMetadata,
}) {
    return {
        type: INDEX_DOCUMENT_LOAD_DOCUMENT,
        payload: {
            document,
            documentTypeId,
            documentTypes,
            binder,
            indexingType,
            binderIndexingMetadata,
        },
    };
}

function writeFailedDocumentLoad() {
    return {
        type: INDEX_DOCUMENT_FAILED_LOAD_DOCUMENT,
    };
}

function writeDescriptorsForDocumentType(descriptors, binderIndexingMetadata) {
    return {
        type: INDEX_DOCUMENT_LOAD_DESCRIPTORS,
        payload: {
            descriptors,
            binderIndexingMetadata,
        },
    };
}

function writeSelectDocumentTypeId(documentTypeId) {
    return {
        type: INDEX_DOCUMENT_SELECT_DOCUMENT_TYPE,
        payload: documentTypeId,
    };
}

export function writeSaveStart() {
    return {
        type: INDEX_DOCUMENT_SAVESTART,
    };
}

export function writeSaveFailed() {
    return {
        type: INDEX_DOCUMENT_SAVEFAILED,
    };
}

export function writeSaveComplete(shouldRedirectToSDFAUponSuccessfulSave) {
    return {
        type: INDEX_DOCUMENT_SAVECOMPLETE,
        payload: shouldRedirectToSDFAUponSuccessfulSave,
    };
}

function writeAscertainmentLoading(value) {
    return {
        type: INDEX_DOCUMENT_ASCERTAIN_LOADING,
        payload: value,
    };
}

function writeAscertainmentResult(value) {
    return {
        type: INDEX_DOCUMENT_ASCERTAIN_RESULT,
        payload: value,
    };
}

function writeDocumentPermission({
    id,
    binderId,
    documentTypeId,
    isIndexed,
    canInitialIndex,
    canIndex,
    canReindex,
    canManageIntegration
}) {
    return {
        type: INDEX_DOCUMENT_DOCUMENT_PERMISSION,
        payload: {
            id,
            binderId,
            documentTypeId,
            isIndexed,
            canInitialIndex,
            canIndex,
            canReindex,
            canManageIntegration
        },
    };
}

export function updateTriggeringSendingDocumentStatusAction(status) {
    return {
        type: UPDATE_TRIGGERING_SENDING_DOCUMENT_STATUS,
        payload: status
    };
}
